import { ChangeableSubject, LANG, Resp, Translate } from '../models/common_model';
import React, { useState, useEffect, useCallback } from 'react';
import * as _ from 'lodash';
import { httpRequest, restRequest } from '../http';
import { useHistory } from 'react-router';
import { PERMISSIONS, retrievePermissions } from '../models/permission_model';
import { Type } from 'typescript';
import { lang } from 'moment';

export interface LoginData {
  email: string,
  password: string
}

interface RemoveUserInfo {
  name: string;
  admin: boolean;
  permissions: string[];
  for_testing?: boolean;
}

export interface UserInfo {
  name: string;
  language?: LANG;
  admin: boolean;
  permissions: PERMISSIONS;
  availablePages: { name: string, pages: { name: string, url: string }[] }[];
  for_testing?: boolean;
}

const HOME_PAGES = (permissions: PERMISSIONS, for_testing: boolean) => {
  const categories: { [key: string]: { name: string, pages: { name: string, url: string }[] } } = {
    system: {
      name: 'System',
      pages: []
    },
    test: {
      name: 'Testing',
      pages: []
    },
    others: {
      name: '',
      pages: []
    }
  };

  if (permissions.user.view) {
    categories.system.pages.push({ name: 'User', url: '/user' })
  }

  if (permissions.role.view) {
    categories.system.pages.push({ name: 'Role', url: '/role' })
  }

  if (permissions.local_server_auth.view) {
    categories.system.pages.push({ name: 'Local Server Auth', url: '/local_server_auth' })
  }


  if (permissions.svn_repository.view) {
    categories.test.pages.push({ name: 'SVN Result', url: '/svn_repository' })
  }

  if (permissions.file_sync.view) {
    categories.test.pages.push({ name: 'File Sync', url: '/file_sync' })
  }

  if (permissions.testing.view) {
    categories.test.pages.push({ name: 'All Testing Data', url: '/testing_files' })
  }

  if (permissions.analyzed_unit.view) {
    categories.test.pages.push({ name: 'Analyzed Units', url: '/analyzed_unit' })
  }

  if (permissions.update_limit.view) {
    categories.test.pages.push({ name: 'Update Limit', url: '/update_limit' })
  }

  if (permissions.testing.setting.other_sequence_parameters.view) {
    categories.test.pages.push({ name: 'Other Sequence Parameters', url: '/other_sequence_parameters' })
  }

  if (permissions.file_sync.delete_file.view) {
    categories.test.pages.push({ name: 'Deleting Testing Files', url: '/file_sync/deleting_testing_file' })
  }

  if (permissions.testing.view) {
    categories.test.pages.push({ name: 'Export CSV by Serials', url: '/export_csv_by_serials' })
  }

  if (permissions.refurbishment.view) {
    categories.test.pages.push({ name: 'Refurbishment', url: '/refurbishment' })
  }

  if (permissions.testing.setting.filter.view) {
    categories.test.pages.push({ name: 'Custom Filters', url: '/testing_data_filter' })
  }

  if (permissions.testing.setting.batch_serial.view) {
    categories.test.pages.push({ name: 'Batch Serial Settings', url: '/testing_data_batch_serial' })
  }

  if (permissions.rma_tracking.view) {
    categories.others.pages.push({ name: 'RMA Tracking', url: '/rma_tracking' })
  }

  if (permissions.equipment.view) {
    categories.others.pages.push({ name: 'Inventory', url: '/inventory' })
  }

  if (permissions.issue_overview.view) {
    categories.others.pages.push({ name: 'Issue Overview', url: '/issue_overview' })
  }

  if (permissions.testing.matching.view) {
    categories.others.pages.push({ name: 'Matching', url: '/matchings' })
  }
  
  return Object.values(categories);
}

const currentUser = new ChangeableSubject<UserInfo>({ name: '', permissions: retrievePermissions(p => false), admin: false, availablePages: [] });

export function useCurrentUser() {
  const [state, setState] = useState<UserInfo>(currentUser.value);
  const history = useHistory();

  useEffect(() => {
    const onChange = (info: UserInfo) => {
      setState(info);
    };

    currentUser.onChange(onChange);

    return () => {
      currentUser.offChange(onChange);
    };
  }, []);

  if (!currentUser.isInited) {
    if (!currentUser.isIniting) {
      currentUser.isIniting = true;
      restRequest<RemoveUserInfo>('auth/login', null, 'get').then(resp => {
        const permissions = retrievePermissions(p => resp.admin || resp.permissions.includes(p));
        currentUser.value = { ...resp, permissions, availablePages: HOME_PAGES(permissions, resp.for_testing || false) }
        if (window.location.pathname.startsWith('/login')) {
          let lastRoute = localStorage.getItem('last_route') || '/';

          if (lastRoute.startsWith('/login')) {
            lastRoute = '/'
          }

          localStorage.removeItem('last_route');
          history.push(lastRoute);
        }
        currentUser.isInited = true;
      }, () => {
      }).finally(() => currentUser.isIniting = false);
    }
  }

  return state;
}

const LANGUAGES: any = {}

export function useLocal(language?: LANG): [Translate, LANG] {
  const [local, setLocal] = useState<any>({});

  useEffect(() => {
    if (language === 'cn') {
      if (!LANGUAGES.cn) {
        //disable it
        LANGUAGES.cn = {} //require('../assets/languages/cn.json');
      }
      setLocal(LANGUAGES.cn);

    } else {
      setLocal({})
    }
  }, [language]);

  const t = useCallback((text: string | string[], skipIndexes?: number[]) => {
    if (Array.isArray(text)) {
      return text.map((t, i) => {
        if (!t.trim()) {
          return t;
        }else if (skipIndexes && skipIndexes.includes(i)) {
          return t;
        }
        return local[t.toLowerCase()] || t;
      }).join('')
    } else {
      return (local[text.toLowerCase()] || text) as string;
    }
  }, [local]);

  return [t, language || 'en'];
}

export function login(data: LoginData) {
  return restRequest<RemoveUserInfo>('auth/login', data, 'post').then(resp => {
    const permissions = retrievePermissions(p => resp.admin || resp.permissions.includes(p));
    currentUser.value = { ...resp, permissions, availablePages: HOME_PAGES(permissions, resp.for_testing || false) }
    return currentUser.value;
  });
}

export function logout() {
  return restRequest<RemoveUserInfo>('auth/logout', null, 'post').then(resp => {
    currentUser.value = { name: '', permissions: retrievePermissions(p => false), admin: false, availablePages: [] };
    currentUser.isInited = false;
    return resp;
  });
}